import { defaultTimeZone, defaultLocales } from './constants';
import { isDate } from './isDate';
import { isInvalidDate } from './isInvalidDate';

/**
 * Converts a Date object into a date and time string
 * for use in a sentence.
 *
 * e.g. June 3, 2021, 11:52 AM
 *
 * NOTE: you should probably use timeAgoString when trying to display
 * an updated/created timestamp.
 *
 * @param {*} date - a Date object you want to convert into a string - gracefully handles any value
 * @param {object} options -
 * @param {string} options.timeZone - the timezone you want date displayed in, defaults to current users time zone
 * @returns {string} a formatted date string
 */
export const dateTimeStringForSentence = (
  date: unknown,
  { timeZone = defaultTimeZone }: DateOnlyStringOptions = {},
): string => {
  if (!isDate(date) || isInvalidDate(date)) {
    return '';
  }

  try {
    const { year, month, day, hour, minute, dayPeriod } = new Intl.DateTimeFormat(defaultLocales, {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZone,
    })
      .formatToParts(date)
      .reduce<Partial<Record<Intl.DateTimeFormatPartTypes, string>>>((acc, part) => {
        if (part.type !== 'literal') {
          acc[part.type] = part.value;
        }
        return acc;
      }, {}) as Record<Intl.DateTimeFormatPartTypes, string>;
    return `${month} ${day}, ${year}, ${hour}:${minute} ${dayPeriod}`;
  } catch (error: unknown) {
    console.error(error); // eslint-disable-line no-console
    return ''; // handle bad input
  }
};
